import { GET, POST } from '../../../api/apiHandler';
import { getAuthenticationHeaders } from '../../../api/utils';
import { API_BASE_URL, AGENT_PLATFORM_BASE_URL, QUERYBUILDER_CONFIG } from '../../../globals';
import {
  AGENT_PLATFORM__AUTO_RESOLUTION,
  AGENT_PLATFORM__ACTIVITY_FEED,
  AGENT_PLATFORM__TASK_CONVERSATION,
  AGENT_PLATFORM__DASHBOARD_AGENTS,
  INCIDENT_ALERT_TICKET,
  INCIDENT_ALERT_TICKET_STATUS_COUNTS,
  INCIDENT_ALERT_TICKET_GROUPED_COUNTS,
  INCIDENT_ALERT_METRIC_TYPES,
  INCIDENT_ALERT_TICKET_METADATA,
  INCIDENT_ALERT_ACTIVITY_USER,
  INCIDENT_ALERT_TASK_TYPE_LIST,
  INCIDENT_ALERT_TICKET_CREATE,
  INCIDENT_ALERT_TICKET_CREATE_PRESIGN,
  INCIDENT_ALERT_ALL_PERMITTED_HUBS,
  GET_QUERY_BUILDER_REPORT,
} from '../../../api/routes';
import {
  IGetTasksParams,
  IGetCountsParams,
  IGetGroupedCountsParams,
  IFetchMetricTypesParams,
  IFetchMyActivityFeedParams,
  IFetchAgentDetailParams,
  IReportDataParams,
  ITaskConversationParams,
} from './ControlTower.types';
import { IActivityLogFilters } from './HomeView/AgentActivityLog.types';
import { IAgentHealthFilters } from './HomeView/AgentHealthCard.types';
import { ICreateTaskPayload } from './HomeView/CreateTaskModal/CreateTaskModal.types';
import { buildSearchEmployeeBody, getStringErrorMessage } from './ControlTower.utils';

const getQueryBuilderHeaders = async () => ({
  ...(await getAuthenticationHeaders()),
  'project-name': 'logistics',
});

// ─── Task APIs ────────────────────────────────────────────────────────────────

export const getTasks = async (params: IGetTasksParams) => {
  const url = `${QUERYBUILDER_CONFIG.BACKENDURL}${INCIDENT_ALERT_TICKET}`;
  return GET(url, params, undefined, await getQueryBuilderHeaders(), getStringErrorMessage);
};

export const getTaskStatusCounts = async (params: IGetCountsParams) => {
  const url = `${QUERYBUILDER_CONFIG.BACKENDURL}${INCIDENT_ALERT_TICKET_STATUS_COUNTS}`;
  return GET(url, params, undefined, await getQueryBuilderHeaders(), getStringErrorMessage);
};

export const getTaskGroupedCounts = async (params: IGetGroupedCountsParams) => {
  const url = `${QUERYBUILDER_CONFIG.BACKENDURL}${INCIDENT_ALERT_TICKET_GROUPED_COUNTS}`;
  return GET(url, params, undefined, await getQueryBuilderHeaders(), getStringErrorMessage);
};

// ─── Ticket Metadata API (drives write access) ────────────────────────────────
// Returns the incident ticket metadata, incl. `typeOfUser` (INCIDENT_MANAGEMENT_WRITE
// / _READ) — the backend's per-user write grant, mirrored from agent-fleet-mobile-view.
export const getTicketMetadata = async () => {
  const url = `${QUERYBUILDER_CONFIG.BACKENDURL}${INCIDENT_ALERT_TICKET_METADATA}`;
  return GET(url, undefined, undefined, await getQueryBuilderHeaders());
};

// Active hubs the caller may operate in — the option list for the Object Code field
// on geofence-bound task types.
export const fetchPermittedHubs = async () => {
  const url = `${QUERYBUILDER_CONFIG.BACKENDURL}${INCIDENT_ALERT_ALL_PERMITTED_HUBS}`;
  return GET(url, undefined, undefined, await getQueryBuilderHeaders(), getStringErrorMessage);
};

// ─── Metric Types API ─────────────────────────────────────────────────────────

export const fetchMetricTypes = async (params: IFetchMetricTypesParams) => {
  const url = `${QUERYBUILDER_CONFIG.BACKENDURL}${INCIDENT_ALERT_METRIC_TYPES}?pageCount=20`;
  return GET(url, params, undefined, await getQueryBuilderHeaders(), getStringErrorMessage);
};

// ─── Assignee Search API ──────────────────────────────────────────────────────

export const searchEmployeeByEmail = async (searchQuery: string) => {
  return POST(`${QUERYBUILDER_CONFIG.BACKENDURL}`, buildSearchEmployeeBody(searchQuery), await getQueryBuilderHeaders(), false, getStringErrorMessage);
};

// ─── My Activity Feed API ─────────────────────────────────────────────────────

export const fetchMyActivityFeed = async (params: IFetchMyActivityFeedParams) => {
  const url = `${QUERYBUILDER_CONFIG.BACKENDURL}${INCIDENT_ALERT_ACTIVITY_USER}`;
  return GET(url, params, undefined, await getQueryBuilderHeaders(), getStringErrorMessage);
};

// ─── Agent Activity Log APIs ──────────────────────────────────────────────────

export const fetchActivityFeedData = (params: IActivityLogFilters) => {
  return GET(`${AGENT_PLATFORM_BASE_URL}${AGENT_PLATFORM__ACTIVITY_FEED}`, params);
};

export const fetchTaskConversation = (
  taskId: number | string,
  params?: ITaskConversationParams,
) => {
  return GET(`${AGENT_PLATFORM_BASE_URL}${AGENT_PLATFORM__TASK_CONVERSATION(taskId)}`, params);
};

// ─── Agent Options API ────────────────────────────────────────────────────────

export const fetchAgentOptions = () => {
  return GET(`${AGENT_PLATFORM_BASE_URL}${AGENT_PLATFORM__DASHBOARD_AGENTS}`, { is_active: true });
};

export const fetchAgentDetail = (params: IFetchAgentDetailParams) => {
  return GET(`${AGENT_PLATFORM_BASE_URL}${AGENT_PLATFORM__DASHBOARD_AGENTS}`, params);
};

// ─── Agent Health APIs ────────────────────────────────────────────────────────

export const fetchAgentHealthData = (params: IAgentHealthFilters) => {
  return GET(`${AGENT_PLATFORM_BASE_URL}${AGENT_PLATFORM__AUTO_RESOLUTION}`, params);
};

// ─── Report Data API ────────────────────────────────────────────────────────

export const getReportData = (body: IReportDataParams) => {
  return POST(`${API_BASE_URL}${GET_QUERY_BUILDER_REPORT}`, body);
};


// ─── Create Task APIs ─────────────────────────────────────────────────────────

export const fetchTaskTypes = async (searchQuery: string | null = null, pageSize = 25) => {
  const url = `${QUERYBUILDER_CONFIG.BACKENDURL}${INCIDENT_ALERT_TASK_TYPE_LIST}`;
  const body = { skipTicketCounts: true, searchQuery: searchQuery || null, status: 'enable', offset: 1, pageSize };
  return POST(url, body, await getQueryBuilderHeaders(), false, getStringErrorMessage);
};

export const createTask = async (payload: ICreateTaskPayload) => {
  const url = `${QUERYBUILDER_CONFIG.BACKENDURL}${INCIDENT_ALERT_TICKET_CREATE}`;
  return POST(url, payload, await getQueryBuilderHeaders(), false, getStringErrorMessage);
};

// `options` selects the backend's custom-field-media branch, which applies the
// per-media-type size limit and MIME whitelist and writes to its own S3 prefix.
// Omitted, the request is treated as a plain ticket attachment.
export const getCreateTaskPresignUrl = async (
  file: { name: string; size: number; mimeType: string },
  options?: { uploadContext: 'attachment' | 'custom-field-media'; mediaType?: 'image' | 'video' },
) => {
  const url = `${QUERYBUILDER_CONFIG.BACKENDURL}${INCIDENT_ALERT_TICKET_CREATE_PRESIGN}`;
  return POST(url, { ...file, ...(options || {}) }, await getQueryBuilderHeaders(), false, getStringErrorMessage);
};
